import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import API, { UPLOADS_URL } from '../api/axios';
import SEO from '../components/SEO';
import PageBanner from '../components/PageBanner';
import Reveal from '../components/ui/Reveal';

const Blog = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [recentPosts, setRecentPosts] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  const [bannerImage, setBannerImage] = useState(null);
  const [bannerReady, setBannerReady] = useState(false);

  useEffect(() => {
    const fetchBlogs = async () => {
      setLoading(true);
      try {
        let url = `/blogs/public?limit=12&page=${page}`;
        if (searchTerm) url += `&search=${encodeURIComponent(searchTerm)}`;
        if (activeCategory) url += `&category=${encodeURIComponent(activeCategory)}`;
        const res = await API.get(url);
        const data = res.data.data || [];
        setBlogs(data);
        setTotalPages(res.data.totalPages || Math.ceil((res.data.total || data.length || 1) / 12) || 1);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    fetchBlogs();
  }, [page, searchTerm, activeCategory]);

  useEffect(() => {
    API.get('/blogs/categories/public').then(res => setCategories(res.data.data || [])).catch(() => {});
    API.get('/blogs/latest').then(r => setRecentPosts(r.data.data || [])).catch(() => {});
    API.get('/pages/public/article').then(res => {
      if (res.data?.data?.banner_image) setBannerImage(res.data.data.banner_image);
    }).catch(() => {}).finally(() => setBannerReady(true));
  }, []);

  const handleSearch = (e) => { e.preventDefault(); setSearchTerm(searchInput); setPage(1); };
  const handleCategoryClick = (cat) => { setActiveCategory(cat === activeCategory ? null : cat); setPage(1); };
  const handlePageChange = (p) => { setPage(p); window.scrollTo({ top: 500, behavior: 'smooth' }); };

  const stripHtml = (html) => html?.replace(/<[^>]*>/g, '') || '';
  const truncate = (text, len) => text.length > len ? text.substring(0, len) + '...' : text;

  const featured = blogs.length > 0 ? blogs[0] : null;
  const regularBlogs = blogs.length > 1 ? blogs.slice(1) : [];

  const tagCloud = (() => {
    const tagMap = {};
    blogs.forEach(blog => {
      if (blog.tags) {
        const tags = Array.isArray(blog.tags) ? blog.tags : String(blog.tags).split(',').map(t => t.trim());
        tags.forEach(t => { if (t) tagMap[t] = (tagMap[t] || 0) + 1; });
      }
    });
    if (Object.keys(tagMap).length === 0) {
      categories.forEach(c => { const name = c.name || c; if (name) tagMap[name] = c.count || 1; });
    }
    return Object.entries(tagMap).sort((a, b) => b[1] - a[1]).slice(0, 15);
  })();
  const maxTagCount = tagCloud.length > 0 ? Math.max(...tagCloud.map(([, c]) => c)) : 1;
  const getTagSize = (count) => { const r = count / maxTagCount; if (r > 0.7) return 'text-sm'; if (r > 0.4) return 'text-xs'; return 'text-[11px]'; };
  const getTagOpacity = (count) => { const r = count / maxTagCount; if (r > 0.7) return 'opacity-100'; if (r > 0.4) return 'opacity-75'; return 'opacity-55'; };

  if (loading && blogs.length === 0) return <div className="loading-screen"><i className="fas fa-spinner fa-spin"></i></div>;

  const formatDate = (d) => new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  const formatDateShort = (d) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  return (
    <>
      <SEO title="Blog" description="Read the latest blog posts from St. Andrews World School, Siddharth Vihar about education, school activities, and student achievements." />
      <PageBanner title="Blog" subtitle="Insights, stories & updates from St. Andrews" breadcrumbs={[{ label: 'Home', link: '/' }, { label: 'Blog' }]} backgroundImage={bannerImage ? `${UPLOADS_URL}/${bannerImage}` : null} fallback={`${process.env.PUBLIC_URL}/images/page-banners/blog.jpg`} loading={!bannerReady} />

      <section className="section-white py-16 md:py-20">
        <div className="container">
          {blogs.length === 0 && !loading ? (
            <div className="text-center py-20">
              <i className="fas fa-blog text-5xl text-muted/40 mb-4"></i>
              <h3 className="text-2xl font-semibold mb-2">No Blog Posts</h3>
              <p className="text-muted text-center">Blog posts will appear here once published.</p>
              {(searchTerm || activeCategory) && (
                <button onClick={() => { setSearchTerm(''); setSearchInput(''); setActiveCategory(null); setPage(1); }}
                  className="mt-4 px-6 py-2 bg-secondary text-white rounded-full text-sm hover:bg-secondary-light transition-colors">
                  <i className="fas fa-times mr-2"></i>Clear Filters
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 xl:gap-14">

              {/* MAIN */}
              <div className="lg:col-span-8 space-y-10">

                {/* Featured */}
                {featured && (
                  <Reveal>
                    <Link to={`/blog/${featured.slug}`} className="group block bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-xl transition-all duration-500">
                      <div className="relative">
                        <div className="aspect-[16/9] overflow-hidden bg-gray-100">
                          <img src={featured.image ? `${UPLOADS_URL}/${featured.image}` : 'https://via.placeholder.com/1200x600?text=No+Image'}
                            alt={stripHtml(featured.title)} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                        </div>
                        {featured.category && (
                          <span className="absolute top-4 left-4 px-3 py-1 bg-secondary text-white text-xs font-semibold rounded-full shadow-md">
                            {featured.category}
                          </span>
                        )}
                      </div>
                      <div className="p-6">
                        <div className="flex items-center gap-4 text-muted text-xs mb-2.5">
                          <span><i className="far fa-user mr-1.5"></i>{featured.author || 'Admin'}</span>
                          <span className="w-1 h-1 rounded-full bg-muted/40"></span>
                          <span><i className="far fa-calendar mr-1.5"></i>{formatDate(featured.created_at)}</span>
                        </div>
                        <h2 className="text-primary text-xl md:text-2xl font-bold leading-tight mb-3">
                          {stripHtml(featured.title)}
                        </h2>
                        <p className="text-body text-sm leading-relaxed mb-4 line-clamp-2">
                          {truncate(stripHtml(featured.content), 200)}
                        </p>
                        <span className="inline-flex items-center gap-2 text-secondary font-semibold text-sm group-hover:gap-3 transition-all">
                          Read Article <i className="fas fa-arrow-right text-xs"></i>
                        </span>
                      </div>
                    </Link>
                  </Reveal>
                )}

                {/* Filters */}
                <Reveal>
                  <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                      <div className="flex flex-wrap gap-2">
                        <button onClick={() => handleCategoryClick(null)}
                          className={`px-4 py-1.5 rounded-full border text-sm font-medium transition-all duration-200 ${
                            !activeCategory ? 'bg-primary text-white border-primary' : 'border-gray-200 text-muted hover:bg-primary hover:text-white hover:border-primary'
                          }`}>All</button>
                        {categories.map((cat) => {
                          const name = cat.name || cat;
                          return (
                            <button key={name} onClick={() => handleCategoryClick(name)}
                              className={`px-4 py-1.5 rounded-full border text-sm font-medium transition-all duration-200 ${
                                activeCategory === name ? 'bg-primary text-white border-primary' : 'border-gray-200 text-muted hover:bg-primary hover:text-white hover:border-primary'
                              }`}>{name}</button>
                          );
                        })}
                      </div>
                      <form onSubmit={handleSearch} className="relative min-w-[220px]">
                        <input type="text" placeholder="Search articles..." value={searchInput}
                          onChange={(e) => setSearchInput(e.target.value)}
                          className="w-full pl-10 pr-4 py-2 rounded-full border border-gray-200 bg-white text-sm text-body placeholder-muted/60 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all" />
                        <button type="submit" className="absolute left-3 top-1/2 -translate-y-1/2 text-muted hover:text-primary transition-colors">
                          <i className="fas fa-search text-sm"></i>
                        </button>
                      </form>
                    </div>
                  </div>
                </Reveal>

                {/* Blog Grid */}
                {regularBlogs.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    {regularBlogs.map((blog, idx) => (
                      <Reveal key={blog.id} delayMs={idx * 60}>
                        <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-lg transition-all duration-500 group">
                          <Link to={`/blog/${blog.slug}`} className="block relative overflow-hidden">
                            <div className="aspect-[16/10] bg-gray-100 overflow-hidden">
                              <img src={blog.image ? `${UPLOADS_URL}/${blog.image}` : 'https://via.placeholder.com/400x250?text=No+Image'}
                                alt={stripHtml(blog.title)} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                            </div>
                            {blog.category && (
                              <span className="absolute top-3 left-3 px-2.5 py-0.5 bg-secondary text-white text-[11px] font-semibold rounded-full shadow-md">
                                {blog.category}
                              </span>
                            )}
                          </Link>
                          <div className="p-5">
                            <div className="flex items-center gap-3 text-muted text-xs mb-2.5">
                              <span><i className="far fa-user mr-1"></i>{blog.author || 'Admin'}</span>
                              <span className="w-1 h-1 rounded-full bg-muted/40"></span>
                              <span><i className="far fa-calendar mr-1"></i>{formatDateShort(blog.created_at)}</span>
                            </div>
                            <h3 className="text-primary font-semibold text-[15px] leading-snug mb-2 line-clamp-2">
                              <Link to={`/blog/${blog.slug}`} className="hover:text-secondary transition-colors">{stripHtml(blog.title)}</Link>
                            </h3>
                            <p className="text-body text-sm leading-relaxed mb-4 line-clamp-2">
                              {truncate(stripHtml(blog.content), 130)}
                            </p>
                            <Link to={`/blog/${blog.slug}`}
                              className="inline-flex items-center gap-1.5 text-secondary text-sm font-semibold hover:gap-2.5 transition-all">
                              Read More <i className="fas fa-arrow-right text-xs transition-transform duration-300 group-hover:translate-x-1"></i>
                            </Link>
                          </div>
                        </div>
                      </Reveal>
                    ))}
                  </div>
                ) : featured ? (
                  <div className="text-center py-16 bg-white rounded-2xl border border-gray-100">
                    <i className="fas fa-filter text-3xl text-muted/40 mb-3"></i>
                    <p className="text-body text-lg font-medium text-center">No more posts in this category.</p>
                    <button onClick={() => { setActiveCategory(null); setSearchTerm(''); setSearchInput(''); setPage(1); }}
                      className="mt-4 px-6 py-2.5 bg-secondary text-white rounded-full text-sm font-semibold hover:bg-secondary-light transition-colors shadow-md hover:shadow-lg">
                      Clear Filters
                    </button>
                  </div>
                ) : null}

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex justify-center pt-4">
                    <div className="inline-flex items-center gap-2">
                      <button onClick={() => handlePageChange(page - 1)} disabled={page === 1}
                        className="w-10 h-10 rounded-full border border-gray-200 flex items-center justify-center text-muted hover:bg-primary hover:text-white hover:border-primary transition-all disabled:opacity-30 disabled:cursor-not-allowed">
                        <i className="fas fa-chevron-left text-xs"></i>
                      </button>
                      {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                        let p;
                        if (totalPages <= 7) p = i + 1;
                        else if (page <= 4) p = i + 1;
                        else if (page >= totalPages - 3) p = totalPages - 6 + i;
                        else p = page - 3 + i;
                        return (
                          <button key={p} onClick={() => handlePageChange(p)}
                            className={`w-10 h-10 rounded-full text-sm font-medium transition-all duration-200 ${
                              p === page ? 'bg-primary text-white shadow-md' : 'border border-gray-200 text-muted hover:bg-primary/10 hover:text-primary'
                            }`}>{p}</button>
                        );
                      })}
                      {totalPages > 7 && page < totalPages - 3 && (
                        <span className="text-muted px-1">...</span>
                      )}
                      {totalPages > 7 && page < totalPages - 3 && (
                        <button onClick={() => handlePageChange(totalPages)}
                          className="w-10 h-10 rounded-full border border-gray-200 text-muted hover:bg-primary/10 hover:text-primary transition-all text-sm font-medium">{totalPages}</button>
                      )}
                      <button onClick={() => handlePageChange(page + 1)} disabled={page === totalPages}
                        className="w-10 h-10 rounded-full border border-gray-200 flex items-center justify-center text-muted hover:bg-primary hover:text-white hover:border-primary transition-all disabled:opacity-30 disabled:cursor-not-allowed">
                        <i className="fas fa-chevron-right text-xs"></i>
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* SIDEBAR */}
              <aside className="lg:col-span-4 space-y-8">
                <Reveal>
                  <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                    <h3 className="text-primary font-bold text-lg mb-4 pb-3 border-b-2 border-secondary/30">Categories</h3>
                    {categories.length === 0 ? (
                      <p className="text-muted text-sm">No categories yet.</p>
                    ) : (
                      <ul className="space-y-1.5">
                        {categories.map((cat) => {
                          const name = cat.name || cat;
                          const count = cat.count || 0;
                          const isActive = activeCategory === name;
                          return (
                            <li key={name}>
                              <button onClick={() => handleCategoryClick(name)}
                                className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-sm transition-all duration-200 ${
                                  isActive ? 'bg-primary/10 text-primary font-semibold' : 'text-body hover:bg-gray-50 hover:text-primary'
                                }`}>
                                <span>{name}</span>
                                {count > 0 && (
                                  <span className={`text-xs px-2 py-0.5 rounded-full ${isActive ? 'bg-primary/20 text-primary' : 'bg-gray-100 text-muted'}`}>{count}</span>
                                )}
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                </Reveal>

                <Reveal delayMs={100}>
                  <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                    <h3 className="text-primary font-bold text-lg mb-4 pb-3 border-b-2 border-secondary/30">Recent Posts</h3>
                    {recentPosts.length === 0 ? (
                      <p className="text-muted text-sm">No recent posts.</p>
                    ) : (
                      <ul className="space-y-4">
                        {recentPosts.slice(0, 5).map((post) => (
                          <li key={post.id}>
                            <Link to={`/blog/${post.slug}`} className="flex items-start gap-3 group">
                              <div className="w-14 h-14 rounded-xl overflow-hidden flex-shrink-0 bg-gray-100 shadow-sm">
                                {post.image ? (
                                  <img src={`${UPLOADS_URL}/${post.image}`} alt={stripHtml(post.title)}
                                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110" />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center text-muted/30"><i className="fas fa-file-alt"></i></div>
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-primary leading-snug group-hover:text-secondary transition-colors line-clamp-2">{stripHtml(post.title)}</p>
                                <span className="text-[11px] text-muted mt-1 block">{formatDateShort(post.created_at)}</span>
                              </div>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </Reveal>

                <Reveal delayMs={200}>
                  <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                    <h3 className="text-primary font-bold text-lg mb-4 pb-3 border-b-2 border-secondary/30">Tags</h3>
                    {tagCloud.length === 0 ? (
                      <p className="text-muted text-sm">No tags yet.</p>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {tagCloud.map(([tag, count]) => (
                          <button key={tag} onClick={() => { setSearchTerm(tag); setSearchInput(tag); setPage(1); }}
                            className={`${getTagSize(count)} ${getTagOpacity(count)} px-3 py-1.5 rounded-full border border-gray-200 text-muted hover:bg-primary hover:text-white hover:border-primary transition-all duration-200 font-medium`}>
                            {tag}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </Reveal>
              </aside>
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default Blog;
